import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {ViewFault} from '@docma-mm-core/fault/fault';
import {Project} from '@docma-mm-core/project/project';
import {Structure} from '@docma-mm-core/structure/structure';
import {Attachment} from '@docma-mm-core/attachment/attachment';

export interface IOptionsStepData {
  duplicateType: 'single' | 'multiple';
  duplicatesToCreate?: number;
}

export interface ILocationStepData {
  selectedStructures: Structure[];
}

export interface IAttachmentStepData {
  selectedAttachments: Attachment[];
  allAttachments: Attachment[];
  selectAllState: boolean;
}

export interface IDuplicateWizardStepper {
  originalFault: ViewFault;
  project: Project;
  optionsStep: IOptionsStepData;
  locationStep: ILocationStepData;
  attachmentStep: IAttachmentStepData;
}

@Injectable()
export class FaultDuplicateWizardStepperService {
  private stepperDataSubject = new BehaviorSubject<IDuplicateWizardStepper>({
    originalFault: null,
    project: null,
    optionsStep: {
      duplicateType: 'multiple',
      duplicatesToCreate: null
    },
    locationStep: {
      selectedStructures: []
    },
    attachmentStep: {
      selectedAttachments: [],
      allAttachments: [],
      selectAllState: false
    }
  });

  stepperData$ = this.stepperDataSubject.asObservable();

  initialize(fault: ViewFault, project: Project): void {
    this.reset();
    this.stepperDataSubject.next({
      ...this.stepperDataSubject.value,
      originalFault: fault,
      project: project
    });
  }

  updateOptionsStep(optionsData: Partial<IOptionsStepData>): void {
    const currentData = this.stepperDataSubject.value;
    const newData = {
      ...currentData,
      optionsStep: {
        ...currentData.optionsStep,
        ...optionsData
      }
    };
    this.stepperDataSubject.next(newData);
  }

  getOptionsStep(): IOptionsStepData {
    return this.stepperDataSubject.value.optionsStep;
  }

  updateLocationStep(locationData: Partial<ILocationStepData>): void {
    const currentData = this.stepperDataSubject.value;
    this.stepperDataSubject.next({
      ...currentData,
      locationStep: {
        ...currentData.locationStep,
        ...locationData
      }
    });
  }

  addLocation(structure: Structure): void {
    const currentData = this.stepperDataSubject.value;
    const currentStructures = currentData.locationStep.selectedStructures;
    
    if (!currentStructures.find(s => s.Id === structure.Id)) {
      this.updateLocationStep({
        selectedStructures: [...currentStructures, structure]
      });
    }
  }

  removeLocation(structure: Structure): void {
    const currentData = this.stepperDataSubject.value;
    const updatedStructures = currentData.locationStep.selectedStructures.filter(s => s.Id !== structure.Id);
    
    this.updateLocationStep({
      selectedStructures: updatedStructures
    });
  }

  setSelectedLocations(structures: Structure[]): void {
    this.updateLocationStep({
      selectedStructures: structures
    });
  }

  getLocationStep(): ILocationStepData {
    return this.stepperDataSubject.value.locationStep;
  }

  updateAttachmentStep(attachmentData: Partial<IAttachmentStepData>): void {
    const currentData = this.stepperDataSubject.value;
    this.stepperDataSubject.next({
      ...currentData,
      attachmentStep: {
        ...currentData.attachmentStep,
        ...attachmentData
      }
    });
  }

  toggleAttachment(attachment: Attachment): void {
    const currentData = this.stepperDataSubject.value;
    const currentAttachments = currentData.attachmentStep.selectedAttachments;
    const existingIndex = currentAttachments.findIndex(a => a.uid === attachment.uid);

    let updatedAttachments: Attachment[];
    if (existingIndex > -1) {
      updatedAttachments = currentAttachments.filter(a => a.uid !== attachment.uid);
    } else {
      updatedAttachments = [...currentAttachments, attachment];
    }

    this.updateAttachmentStep({
      selectedAttachments: updatedAttachments
    });
  }

  selectAllAttachments(attachments: Attachment[]): void {
    this.updateAttachmentStep({
      selectedAttachments: [...attachments],
      selectAllState: true
    });
  }

  deselectAllAttachments(): void {
    this.updateAttachmentStep({
      selectedAttachments: [],
      selectAllState: false
    });
  }

  getAttachmentStep(): IAttachmentStepData {
    return this.stepperDataSubject.value.attachmentStep;
  }

  isOptionsStepValid(): boolean {
    const options = this.getOptionsStep();
    if (!options.duplicateType) return false;
    
    if (options.duplicateType === 'single') {
      return options.duplicatesToCreate !== null && 
             options.duplicatesToCreate !== undefined &&
             !isNaN(options.duplicatesToCreate) &&
             options.duplicatesToCreate >= 1 && 
             options.duplicatesToCreate <= 100;
    }
    
    return true;
  }

  isLocationStepValid(): boolean {
    const location = this.getLocationStep();
    const options = this.getOptionsStep();
    
    if (options.duplicateType === 'multiple') {
      return location.selectedStructures.length > 0;
    } else if (options.duplicateType === 'single') {
      return location.selectedStructures.length === 1;
    }
    
    return false;
  }

  isAttachmentStepValid(): boolean {
    return true;
  }

  getCompleteData(): IDuplicateWizardStepper {
    return this.stepperDataSubject.value;
  }

  reset(): void {
    this.stepperDataSubject.next({
      originalFault: null,
      project: null,
      optionsStep: {
        duplicateType: 'multiple',
        duplicatesToCreate: null
      },
      locationStep: {
        selectedStructures: []
      },
      attachmentStep: {
        selectedAttachments: [],
        allAttachments: [],
        selectAllState: false
      }
    });
  }
}